import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircle2Icon,
  ChevronRightIcon,
  PackageIcon,
  PackagePlusIcon,
  SearchIcon,
  XIcon,
} from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { Button } from '@/shared/ui/buttons/Button';
import { Input } from '@/shared/ui/inputs/Input';
import { Select } from '@/shared/ui/selects/Select';
import { Fab } from '@/shared/ui/buttons/Fab';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { FeaturesParcelsParcelCard } from '@/features/parcels';
import { FeaturesCargoIdParcelCreateGateDialog } from '@/features/cargo-id';
import { apiParcelsConfirmShippingPost, useMyParcels } from '@/entities/parcels';
import { useCargoIdGateStatus } from '@/entities/cargo-id';
import {
  CLIENT_STATUS_VALUES,
  type ApiParcelsGetItem,
  type ClientStatus,
} from '@/shared/types/api/parcels';

// Owner spec (2026-05-31): when no status filter is active, parcels are sorted
// by workflow priority — newest activity at the top — then by created_at desc
// within the same priority bucket. The numeric weight mirrors
// CLIENT_STATUS_VALUES order, so adding new statuses to the enum keeps this in
// sync as long as the order is meaningful top → bottom.
const STATUS_PRIORITY: Record<ClientStatus, number> = CLIENT_STATUS_VALUES.reduce(
  (acc, value, index) => {
    acc[value] = index;
    return acc;
  },
  {} as Record<ClientStatus, number>,
);

const priorityOf = (p: ApiParcelsGetItem): number => {
  const cs = p.client_status;
  if (cs && cs in STATUS_PRIORITY) return STATUS_PRIORITY[cs];
  // Fallback: parcels without a client_status sort to the very bottom so they
  // never preempt active workflow buckets.
  return CLIENT_STATUS_VALUES.length;
};

const createdAtDesc = (a: ApiParcelsGetItem, b: ApiParcelsGetItem): number =>
  b.created_at.localeCompare(a.created_at);

// A parcel is "confirmable" (eligible for the bulk Confirm shipping action)
// when it's admin-approved, still in the pre-shipping ``IN_PROGRESS`` workflow
// state, has at least one declaration product, and — 2026-05-31 owner rule —
// is physically present in the JP warehouse (``is_in_warehouse=true``). The
// backend also enforces the warehouse gate; this client check just hides the
// button so the user doesn't fire a request that's guaranteed to be rejected.
const isConfirmable = (p: ApiParcelsGetItem) =>
  p.status === 'APPROVED' &&
  p.client_status === 'IN_PROGRESS' &&
  p.is_in_warehouse === true &&
  (p.products?.length ?? 0) > 0;

// Owner rule (#55, 2026-05-31): client-side page size for the /parcels list.
// The backend endpoint returns the full list in one response, so pagination
// is purely a view concern here.
const PAGE_SIZE = 10;

export const WidgetsParcelsMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const query = useMyParcels();
  const gate = useCargoIdGateStatus();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ClientStatus | ''>('IN_PROGRESS');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gateDialogOpen, setGateDialogOpen] = useState(false);
  const [page, setPage] = useState(1);

  const handleCreateClick = (): void => {
    if (gate.status === 'ready') {
      navigate('/parcels/create');
      return;
    }
    if (gate.status === 'loading') {
      // still loading — surface a brief hint and don't navigate
      toast.info(t('common:loading'));
      return;
    }
    // 'none' | 'error' → open the gate dialog so the user can act
    setGateDialogOpen(true);
  };

  const statusOptions = [
    { value: '', label: t('parcelStatus:all') },
    ...CLIENT_STATUS_VALUES.map((v) => ({ value: v, label: t(`clientStatus:${v}`) })),
  ];

  const filtered = useMemo(() => {
    const list = query.data ?? [];
    const term = search.trim().toLowerCase();
    const matched = list.filter((p) => {
      if (status && p.client_status !== status) return false;
      if (!term) return true;
      return (
        p.name.toLowerCase().includes(term) ||
        p.trek_id.toLowerCase().includes(term) ||
        (p.store_name ?? '').toLowerCase().includes(term)
      );
    });
    // When a status filter is active the bucket is homogeneous, so we just
    // sort newest-first. Otherwise sort by workflow priority then created_at
    // desc so users see the most-relevant active parcels at the top.
    const sorted = matched.slice();
    if (status) {
      sorted.sort(createdAtDesc);
    } else {
      sorted.sort((a, b) => {
        const pa = priorityOf(a);
        const pb = priorityOf(b);
        if (pa !== pb) return pa - pb;
        return createdAtDesc(a, b);
      });
    }
    return sorted;
  }, [query.data, search, status]);

  // Client-side pagination — keep the page in range as filters change so the
  // user doesn't end up stuck on an empty page after typing a search term.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const pageStart = (page - 1) * PAGE_SIZE;
  const paged = useMemo(
    () => filtered.slice(pageStart, pageStart + PAGE_SIZE),
    [filtered, pageStart],
  );

  const hasConfirmable = useMemo(
    () => (query.data ?? []).some(isConfirmable),
    [query.data],
  );

  const confirm = useMutate({
    api: (ids: string[]) => apiParcelsConfirmShippingPost({ parcel_ids: ids }),
    invalidateKeys: [queryKeys.parcels.all],
    onSuccess: (res) => {
      toast.success(t('parcels:confirmShipping.toast.success', { count: res.confirmed }));
      if (res.skipped.length > 0) {
        toast.info(t('parcels:confirmShipping.toast.skipped', { count: res.skipped.length }));
      }
      setSelectedIds([]);
      setSelectMode(false);
      setConfirmOpen(false);
    },
    onError: (_e, message) => {
      toast.error(message);
      setConfirmOpen(false);
    },
  });

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  useEffect(() => {
    if (!hasConfirmable && selectMode) exitSelectMode();
  }, [hasConfirmable, selectMode]);

  return (
    <>
      <PageHeader title={t('parcels:title')} />

      <div className="flex flex-col gap-3 p-4 lg:gap-4 lg:p-6">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="min-w-0 flex-1">
            <Input
              placeholder={t('parcels:searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<SearchIcon className="h-4 w-4" />}
            />
          </div>
          <div className="sm:w-56">
            <Select
              options={statusOptions}
              value={status}
              onChange={(e) => setStatus(e.target.value as ClientStatus | '')}
            />
          </div>
        </div>
        {hasConfirmable && (
          selectMode ? (
            <Button
              variant="outline"
              leftIcon={<XIcon className="h-4 w-4" />}
              onClick={exitSelectMode}
              className="w-full sm:w-auto sm:self-start"
            >
              {t('common:cancel')}
            </Button>
          ) : (
            <Button
              variant="primary"
              leftIcon={<CheckCircle2Icon className="h-4 w-4" />}
              onClick={() => setSelectMode(true)}
              className="w-full sm:w-auto sm:self-start"
            >
              {t('parcels:confirmShipping.select')}
            </Button>
          )
        )}

        {query.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('parcels:loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<PackageIcon className="h-10 w-10" />}
            title={query.data?.length ? t('parcels:emptyFiltered') : t('parcels:emptyTitle')}
            description={t('parcels:emptyDescription')}
            action={
              <Button onClick={handleCreateClick} leftIcon={<PackagePlusIcon className="h-4 w-4" />}>
                {t('parcels:createParcel')}
              </Button>
            }
          />
        ) : (
          <>
            <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3 lg:gap-4">
              {paged.map((p) => {
                const selectable = selectMode && isConfirmable(p);
                return (
                  <li key={p.id} className={selectMode && !selectable ? 'opacity-50' : undefined}>
                    <FeaturesParcelsParcelCard
                      parcel={p}
                      selectable={selectable}
                      selected={selectedIds.includes(p.id)}
                      onToggleSelect={toggleSelect}
                    />
                  </li>
                );
              })}
            </ul>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  leftIcon={<ArrowLeftIcon className="h-4 w-4" />}
                >
                  {t('common:back')}
                </Button>
                <span className="text-sm text-ink-muted">{page} / {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  rightIcon={<ChevronRightIcon className="h-4 w-4" />}
                >
                  {t('common:next')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {selectMode && selectedIds.length > 0 ? (
        <div className="fixed bottom-24 left-4 right-4 z-30 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-card backdrop-blur md:bottom-6 md:left-1/2 md:right-auto md:w-[28rem] md:-translate-x-1/2">
          <span className="text-sm text-ink-muted">
            {t('parcels:confirmShipping.selectedCount', { count: selectedIds.length })}
          </span>
          <Button
            className="ml-auto"
            leftIcon={<CheckCircle2Icon className="h-4 w-4" />}
            loading={confirm.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {t('parcels:confirmShipping.action')}
          </Button>
        </div>
      ) : (
        !selectMode && (
          <Fab
            label={t('parcels:createParcel')}
            icon={<PackagePlusIcon className="h-6 w-6" aria-hidden />}
            onClick={handleCreateClick}
          />
        )
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={t('parcels:confirmShipping.confirmTitle')}
        description={t('parcels:confirmShipping.confirmDescription')}
        confirmLabel={t('parcels:confirmShipping.confirmAction')}
        loading={confirm.isPending}
        onCancel={() => {
          if (!confirm.isPending) setConfirmOpen(false);
        }}
        onConfirm={() => confirm.mutate(selectedIds)}
      />

      <FeaturesCargoIdParcelCreateGateDialog
        open={gateDialogOpen}
        onClose={() => setGateDialogOpen(false)}
      />
    </>
  );
};
